import { ClientResponse, FlagsBucket, IdName } from '@key-telematics/fleet-api-client';
import { FeatureSection } from 'app/app.features';
import { FormBuilderField } from 'app/shared/components/form-builder';
import  Bluebird from 'bluebird';
import { cloneDeep } from 'lodash';
import { AdminEditorService } from '../../admin.service';
import { FormSectionHandler } from '../entity.handler';
import { EMPTY_GUID, EntityUtils } from '../utils';
import { AdminEntityTabsHandler, ExternalTabsHandler, OverviewTabsHandler, ScoringThresholdsHandler } from './app-features-list-handlers';

interface EditorValues {
    [key: string]: any;
}

export class FeatureFlagsSectionHandler<T extends ClientResponse> implements FormSectionHandler<T, EditorValues> {

    type = 'form';
    icon = this.feature.icon;
    bulk = true;
    hidden = false;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    defaultValues = {};
    inheritedValues = {};

    listHandlers = {
        'page-external-tabs': new ExternalTabsHandler(this.admin),
        'page-admin-customPanels': new AdminEntityTabsHandler(this.admin),
        'page-overview-customPanels': new OverviewTabsHandler(this.admin),
        'page-replay-thresholds': new ScoringThresholdsHandler(this.admin),
    };

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,

        private feature: FeatureSection,
        private defaults: any,
        private featureBucket: string,

        private updateCallback: (entity: any, payload: any) => Promise<any>
    ) {
    }

    translate(key: string, values?: object): string {
        return this.admin.i18n.instant(`FEATURES.${this.featureBucket.toUpperCase()}.${this.feature.page.toUpperCase()}.${key}`, values);
    }

    applyUpdate(entity: any, payload: any): Promise<any> {
        return this.updateCallback(entity, payload);
    }

    async getCompanyGroupMergedFlags(groupId: string): Promise<any> {
        const result = {};
        let group = groupId !== EMPTY_GUID ? await this.admin.getCached(`getCompanyGroup/${groupId}`, api => api.accounts.getCompanyGroup(groupId), 3) : null;
        const groups = [];
        while (group) {
            groups.push(group);
            group = group.parent && group.parent.id !== 'root' ? await this.admin.getCached(`getCompanyGroup/${group.parent.id}`, api => api.accounts.getCompanyGroup(group.parent.id), 3) : null;
        }
        groups.reverse();
        groups.forEach(g => {
            Object.entries(g.flags[this.featureBucket] || {}).forEach(([key, value]) => {
                result[key] = value;
            });
        });
        return result;
    }

    async mapEntityToEditor(entity: { flags?: FlagsBucket, group?: string, parent?: IdName }): Promise<EditorValues> {
        this.inheritedValues = {};
        if (entity.group && entity.group !== EMPTY_GUID) { // this is a company with a group specified
            this.inheritedValues = await this.getCompanyGroupMergedFlags(entity.group);
        }
        if (entity.parent && entity.parent.id !== 'root') { // this is a company group
            this.inheritedValues = await this.getCompanyGroupMergedFlags(entity.parent.id);
        }

        const result = entity.flags[this.featureBucket] || {};
        Object.keys(result).forEach(key => {
            // convert booleans to strings
            if (typeof result[key] === 'boolean') {
                result[key] = result[key].toString();
            }

            const handler = this.listHandlers[key];
            if (handler) {
                handler.mapEntityToEditor(key, result);
            }

        });

                

        return result;
    }

    mapEditorToEntity(entity: ClientResponse, values: EditorValues): Promise<ClientResponse> {
        if (values) {
            entity.flags = entity.flags || {};
            const result = entity.flags[this.featureBucket] || {};
            Object.keys(values).forEach(key => {
                if (values[key] === 'true') { values[key] = true; }
                if (values[key] === 'false') { values[key] = false; }
                if (values[key] === 'null') { values[key] = null; }
                result[key] = values[key];
            });

            Object.keys(result).forEach(key => {
                const handler = this.listHandlers[key];
                if (handler) {
                    handler.mapEditorToEntity(key, result);
                }
            });

            // when we set a feature to inherited, we need to remove any sub items from the list or
            // they will continue to overwrite the inherited values. also remove settings for disabled items
            this.feature.fields.forEach(field => {
                if (result[field.id] === null || result[field.id] === false) { // we've just set this field to inherited or disabled
                    const enabledValue = field.values?.find(x => x.key === 'true');
                    if (enabledValue) {
                        (enabledValue.fields || []).forEach(subfield => {
                            result[subfield.id] = null; // delete whatever values were set on the enabled subfields
                        });
                    }
                }
            });
            entity.flags[this.featureBucket] = result;
        }
        return Promise.resolve(entity);
    }

    getMergedValue(id: string, values: any): { value: any, type: string } {
        if (values[id] !== null && values[id] !== undefined) {
            return { value: values[id], type: 'explicit' };
        }
        if (this.inheritedValues[id] !== null && this.inheritedValues[id] !== undefined) {
            return { value: this.inheritedValues[id], type: 'inherited' };
        }
        return { value: this.defaultValues[id], type: 'default' };
    }

    async getFields(entity: ClientResponse): Promise<FormBuilderField[]> {

        const defaults = {};
        Object.entries(this.defaults)
            .filter(([key]) => key.startsWith(this.feature.page + '-'))
            .forEach(([key, value]) => {
                defaults[key] = value;
            });
        this.defaultValues = defaults;

        const parseFields = (fields: FormBuilderField[]): Bluebird<FormBuilderField[]> => {

            return Bluebird.map(fields, async field => {

                field.description = this.translate(field.title + '-DESC');
                if (field.description.endsWith('-DESC')) {
                    field.description = undefined; // if no translation is found, remove the description
                }
                field.title = this.translate(field.title);

                // translate all pre-configured lookup values
                if (field.values && Array.isArray(field.values)) {
                    await Bluebird.map(field.values, async value => {
                        value.value = this.admin.i18n.instant(`FEATURES.STATES.${value.value}`);
                        if (value.fields) {
                            await parseFields(value.fields);
                        }
                    });
                }

                const valueLookup = field['valueLookup']; // bit of hack
                if (valueLookup) {
                    switch (valueLookup) {
                        case 'reports':
                            field.values = await this.admin.getCached(`listReportDefinitions/${this.ownerId}`, api => api.entities.listReportDefinitions(this.ownerId)).then(result => {
                                const reports = result.items.map(x => {
                                    // translate the reports to be displyed correctly. 
                                    const reportPrefix = `${x.source.toUpperCase()}.${x.name.toUpperCase().replace(/[^a-zA-Z\d]/g, '_')}.${x.styles[0].id.toUpperCase()}`;
                                    const display = this.admin.i18n.instant(`REPORTING.ITEMS.${reportPrefix}.NAME`);
                                    return { 
                                        key: x.id, 
                                        value: display ?? x.name
                                    };
                                });
                                return reports.sort((a, b) => a.value.localeCompare(b.value));
                            });
                            break;
                        case 'alert-actions':
                            field.values = [
                                { key: 'request_video', value: this.admin.i18n.instant(`ADMIN.EDITORS.ALERT.ACTIONS.REQUEST_VIDEO`) },
                                { key: 'label_current_trip', value: this.admin.i18n.instant(`ADMIN.EDITORS.ALERT.ACTIONS.LABEL_CURRENT_TRIP`) },
                            ].filter(x => x);
                            break;
                        case 'assetTypes':
                            field.values = await this.admin.getCached(`${this.ownerId}/assetTypes}`, api => api.entities.listAssetTypes(this.ownerId)).then(result => {
                                result.items.sort((a, b) => a.name.localeCompare(b.name));
                                return result.items.map(x => ({ key: x.id, value: x.name }));
                            })
                            break;
                        case 'standardReports':
                            field.values = [
                                {
                                    key: 'state-dailyutilization',
                                    value: this.admin.i18n.instant('REPORTING.LIST.STANDARD.ASSET_STATE_UTILIZATION.NAME'),
                                },
                                {
                                    key: 'trips-listing',
                                    value: this.admin.i18n.instant('REPORTING.LIST.STANDARD.TRIP_HISTORY.NAME'),

                                },
                                {
                                    key: 'trips-dailysummary',
                                    value: this.admin.i18n.instant('REPORTING.LIST.STANDARD.TRIP_HISTORY_SUMMARY.NAME'),

                                },
                                {
                                    key: 'speed-listing',
                                    value: this.admin.i18n.instant('REPORTING.LIST.STANDARD.SPEEDING.NAME'),

                                },
                                {
                                    key: 'geofences-listing',
                                    value: this.admin.i18n.instant('REPORTING.LIST.STANDARD.GEOFENCE.NAME'),

                                },
                                {
                                    key: 'stopping-listing',
                                    value: this.admin.i18n.instant('REPORTING.LIST.STANDARD.STOPPING.NAME'),

                                },
                                {
                                    key: 'utilization-default',
                                    value: this.admin.i18n.instant('REPORTING.LIST.STANDARD.UTILIZATION.NAME'),
                                },
                                {
                                    key: 'utilization-listing',
                                    value: this.admin.i18n.instant('REPORTING.LIST.STANDARD.IDLING.NAME'),
                                },
                                {
                                    key: 'alerts-events-listing',
                                    value: this.admin.i18n.instant('REPORTING.LIST.STANDARD.ALERTS.NAME'),
                                },
                                {
                                    key: 'fuel-listing',
                                    value: this.admin.i18n.instant('REPORTING.LIST.STANDARD.FUEL.NAME'),
                                }
                            ];
                            break;
                    }
                }

                const systemUserSetting = field['systemUserOnly']; // hack
                if (systemUserSetting) {
                    field.visible = this.admin.user.owner.type === 'system';
                }

                field.getValue = (f: FormBuilderField, values: any): any => {
                    const value = values[f.id];

                    if (f.type === 'checklist') {
                        if (Array.isArray(value)) {
                            return value;
                        } else {
                            return value ? value.split(',') : [];
                        }
                    }
                    if (value === true) { return 'true'; }
                    if (value === false) { return 'false'; }
                    if (value === null) { return 'null'; }
                    return value;
                };

                if (field.type === 'checklist') { // convert array to comma delimited string
                    field.setValue = (f, values, value) => {
                        values[f.id] = value.join(',');
                    };
                }

                field.getText = (f: FormBuilderField, values: any): any => {
                    const item = this.getMergedValue(f.id, values);
                    let text = item.value;
                    if (f.type === 'checklist') {
                        const value = item.value.split(',');
                        text = value.map(id => {
                            const lookup = (f.values || []).find(x => x.key === id);
                            // value must be in the lookup, otherwise we filter it out.
                            return lookup ? lookup.value : null;
                        }).filter(value => value).sort().join(', ');
                    } else {
                        const lookup = (f.values || []).find(x => x.key === String(item.value));
                        text = (lookup && lookup.value.toString()) || item.value || '';
                    }
                    return item.type === 'explicit' ? text : '' + text + ' (' + this.admin.i18n.instant(`FEATURES.STATES.${item.type.toUpperCase()}`) + ')';
                };

                if (field.type === 'list') {
                    const handler = this.listHandlers[field.id];
                    if (handler) {
                        handler.init(field);
                    }
                }

                return field;

            });
        };


        let results = cloneDeep(this.feature.fields);

        // TODO: This is temporary. Remove once this becomes available for anyone else but system
        if (this.feature.page === 'page-dashboards' && this.admin.user.owner.type !== 'system') {
            // remove subfields on dashboard enabling
            delete results[0].values[1].fields;
        }

        results = await parseFields(results);

        const blacklist = [];

        if (this.feature.page === 'page-reporting' && this.admin.user.owner.type !== 'system') {
            blacklist.push('page-reporting-dateLimitOverride');
            blacklist.push('page-reporting-renderer');
        }

        const fields = results[0].values[1].fields;
        if (fields) {
            const keys = fields.filter(field => field['systemUserOnly'])?.map(field => field.id);
            if (this.admin.user.owner.type !== 'system') {
                blacklist.push(...keys);
            }
  
            results[0].values[1].fields = fields.filter(x => {
                return !blacklist.includes(x.id);
            });
        }

        return EntityUtils.prepareBulk(!entity, results.map(x => x.id), results);

    }

}
